import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Type,
  ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatTab, MatTabGroup, MatTooltip } from '@angular/material';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef
} from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { ApolloError } from 'apollo-client';
import * as _ from 'lodash';
import { Observable, of, zip } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { Clinic, ClinicService } from 'src/app/core/api/clinic.service';
import { ErrorHandlerService } from 'src/app/core/api/error-handler.service';
import {
  AccessState,
  subscriptionPlanKeyToFriendlyNameMap
} from 'src/app/core/schemas/subscription-plan.schema';
import { SubscriptionPlanAccessService } from 'src/app/core/services/subscription-plan-access.service';
import { FormTemplateSchemaType } from 'src/app/form-template/form-template-model';
import { IntakeFormService } from 'src/app/intake-form/intake-form.service';
import { ConfirmModalService } from 'src/app/shared/confirm-modal/confirm-modal.service';
import {
  CognitoGroup,
  Doctor,
  SubscriptionPlan,
  TemperatureUnit,
  UpdateReferralCenterInput
} from './../../../API';
import { StaffService } from './../../core/api/staff.service';
import { ReferralService } from './../../referral/referral.service';
import { ClinicFormGroup } from './../../shared/consult-forms/clinic-form/clinic-form.model';
import { ClinicSetupService } from './clinic-setup.service';
import { LayoutsService } from './layouts/layouts.service';
import { QuestionnaireReminderComponent } from './questionnaire-reminder/questionnaire-reminder.component';
import { TextFavoriteService } from './text-favorite/text-favorite.service';
import { TreatmentTemplateService } from './treatment-template/treatment-template.service';

interface Units {
  label: string;
  type: string;
  radioOptions?: { label: string; value: TemperatureUnit }[];
}

@Component({
  selector: 'csi-clinic-setup-modal',
  templateUrl: './clinic-setup-modal.component.html',
  styleUrls: ['./clinic-setup-modal.component.css']
})
export class ClinicSetupModalComponent implements OnInit, OnDestroy {
  @ViewChild('tabGroup', { static: true }) private tabGroup: MatTabGroup;
  public readonly clinicFormGroup = new ClinicFormGroup();
  public readonly AccessState = AccessState;
  public tabLabelToIndexMap: { [key: string]: number } = {};
  public formControlErrorMap = {};
  public selectedIndex = 0;
  public component: Type<any> = QuestionnaireReminderComponent;
  public combinedFormGroup = {};
  public subscriptionPlanKeyToFriendlyNameMap = subscriptionPlanKeyToFriendlyNameMap;
  public csiAdminAccess = this.staffService.isStaffPartOfGroup(
    this.staffService.staff || { cognitoGroups: null },
    CognitoGroup.Admin
  );
  public translateLabel = {};


  unitCategories: Units[] = [
    {
      label: 'Temperature',
      type: 'Radio',
      radioOptions: [
        { label: 'Fahrenheit', value: TemperatureUnit.fahrenheit },
        { label: 'Celsius', value: TemperatureUnit.celsius }
      ]
    }
  ];

  public selectedSubscriptionPlan: FormControl = new FormControl();
  public SubscriptionPlan = Object.keys(SubscriptionPlan);

  public triedSave = false;
  public showEmrTab: boolean;
  public shouldShowDryEyeSetupTabs;
  public loggedInDoctor: Doctor;
  public intakeFormTypes: string[] = [];
  public intakeFormTemplateIdToSchemaMap: { [key: string]: any };

  public intakeFormLink = `${window.location.protocol}//${window.location.host}/intake-form/${this.clinicSetupService.clinicId}?country=${this.clinicSetupService.clinic.country}`;

  public readonly copyLink = 'Copy Intake Form Link';
  public readonly copied = 'Copied';
  public toolTipContent = this.copyLink;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { clinic: Clinic; showEmr: boolean; isAdmin: boolean; selectedTabLabel: string },
    private dialogRef: MatDialogRef<ClinicSetupModalComponent, undefined>,
    private clinicService: ClinicService,
    private errorHandlerService: ErrorHandlerService,
    public clinicSetupService: ClinicSetupService,
    private staffService: StaffService,
    private referralService: ReferralService,
    private layoutsService: LayoutsService,
    public translocoService: TranslocoService,
    public textFavoriteService: TextFavoriteService,
    public treatmentTemplateService: TreatmentTemplateService,
    public intakeFormService: IntakeFormService,
    private confirmModalService: ConfirmModalService,
    private changeRef: ChangeDetectorRef,
    public subscriptionPlanAccessService: SubscriptionPlanAccessService
  ) {}

  @ViewChild('tooltip', { static: false }) tooltip: MatTooltip;

  ngOnInit() {
    this.combinedFormGroup = {
      clinicFormGroup: this.clinicFormGroup,
      clinicSetupFormGroup: this.clinicSetupService.clinicSetupFormGroup
    };
    this.clinicSetupService.clinicSetupFormGroup.patchValueFromClinicSetup(
      this.clinicSetupService.clinicSetup,
      this.staffService.staff.email
    );

    this.clinicFormGroup.patchFromClinic(this.data.clinic);
    if (this.clinicSetupService.isClinicAdmin) {
      this.clinicFormGroup.enable();
      this.clinicSetupService.clinicSetupFormGroup.enable();
    } else {
      this.clinicFormGroup.disable();
      this.clinicSetupService.clinicSetupFormGroup.disable();
    }

    this.intakeFormTemplateIdToSchemaMap = {
      ...this.intakeFormService.defaultIntakeFormTemplateSchemas,
      ...(
        this.clinicSetupService.formTemplateTypeToTemplatesMap[FormTemplateSchemaType.intake] || []
      ).reduce((acc, formTemplate) => {
        acc[formTemplate.id] = formTemplate.schema;
        return acc;
      }, [])
    };

    this.loggedInDoctor = this.staffService.staff;
    this.shouldShowDryEyeSetupTabs =
      this.loggedInDoctor &&
      this.staffService.isDoctorApprovedForDryEyeSpecializedForm(this.loggedInDoctor);

    this.layoutsService.setLayouts(
      this.clinicSetupService.clinicSetupFormGroup.controls.layouts.value,
      this.subscriptionPlanAccessService.subscriptionPlanSchema
    );

    this.clinicFormGroup.markAsPristine();
    this.clinicSetupService.clinicSetupFormGroup.markAsPristine();
    this.layoutsService.layoutsFormGroup.markAsPristine();
  }

  ngAfterViewInit() {
    this.tabGroup._tabs.forEach((tab: MatTab, index: number) => {
      this.tabLabelToIndexMap[tab.textLabel] = index;
    });
    if (this.data.selectedTabLabel && this.tabLabelToIndexMap[this.data.selectedTabLabel]) {
      this.selectedIndex = this.tabLabelToIndexMap[this.data.selectedTabLabel];
    }
    this.changeRef.detectChanges();
  }

  noSort() {
    return 0;
  }
  initialTabGroup() {
    const tab: MatTab = this.tabGroup._tabs.toArray()[this.selectedIndex];
    return { index: this.selectedIndex, tab: tab };
  }

  ngOnDestroy() {}

  public static open(
    matDialogService: MatDialog,
    clinic: Clinic,
    showEmr: boolean,
    config: MatDialogConfig<never> = {},
    selectedTabLabel?: string
  ) {
    return matDialogService.open(ClinicSetupModalComponent, {
      width: '1080px',
      maxWidth: '95vw',
      ...config,
      data: { clinic, showEmr, selectedTabLabel }
    });
  }

  public saveAndClose() {
    this.clinicFormGroup.submitted = true;
    this.clinicSetupService.clinicSetupFormGroup.submitted = true;
    this.triedSave = true;
    if (this.clinicFormGroup.invalid || this.clinicSetupService.clinicSetupFormGroup.invalid) {
      this.makeSureFormErrorsAreVisible();
      return;
    }

    if (this.layoutsService.layoutsFormGroup.dirty) {
      this.clinicSetupService.clinicSetupFormGroup.controls.layouts.setValue(
        Object.values(this.layoutsService.layoutsFormGroup.value)
      );

      this.clinicSetupService.clinicSetupFormGroup.controls.layouts.markAsDirty();
    }

    if (this.clinicFormGroup.dirty || this.clinicSetupService.clinicSetupFormGroup.dirty) {
      this.clinicSetupService.isSaving = true;
      this.disableInteraction();

      zip(this.getClinicMutation(), this.getReferralCenterSyncMutation())
        .pipe(
          finalize(() => {
            this.clinicSetupService.isSaving = false;
            this.enableInteraction();
          })
        )
        .subscribe({
          error: (error: ApolloError) => {
            this.errorHandlerService.handleGraphQLError(error);
          },
          complete: () => {
            this.staffService.staff.clinic.logo = this.clinicFormGroup.controls.logo.value;
            this.layoutsService.setLayouts(
              this.clinicSetupService.clinicSetupFormGroup.controls.layouts.value
            );
            this.dialogRef.close();
          }
        });
    } else {
      this.dialogRef.close();
    }
  }

  private hasUnsavedChanges(): boolean {
    return (
      this.clinicFormGroup.dirty ||
      this.clinicSetupService.clinicSetupFormGroup.dirty ||
      this.layoutsService.layoutsFormGroup.dirty
    );
  }

  public save() {
    this.clinicFormGroup.submitted = true;
    this.clinicSetupService.clinicSetupFormGroup.submitted = true;
    this.triedSave = true;

    if (this.clinicFormGroup.invalid || this.clinicSetupService.clinicSetupFormGroup.invalid) {
      this.makeSureFormErrorsAreVisible();
      return;
    }

    if (this.layoutsService.layoutsFormGroup.dirty) {
      this.clinicSetupService.clinicSetupFormGroup.controls.layouts.setValue(
        Object.values(this.layoutsService.layoutsFormGroup.value)
      );

      this.clinicSetupService.clinicSetupFormGroup.controls.layouts.markAsDirty();
    }

    if (this.clinicFormGroup.dirty || this.clinicSetupService.clinicSetupFormGroup.dirty) {
      this.clinicSetupService.isSaving = true;
      this.layoutsService.layoutsFormGroup.disable();
      this.disableInteraction();

      zip(this.getClinicMutation(), this.getReferralCenterSyncMutation())
        .pipe(
          finalize(() => {
            this.layoutsService.layoutsFormGroup.enable();
            this.clinicSetupService.isSaving = false;
            this.enableInteraction();
          })
        )
        .subscribe({
          error: (error: ApolloError) => {
            this.errorHandlerService.handleGraphQLError(error);
          },
          complete: () => {
            this.staffService.staff.clinic.logo = this.clinicFormGroup.controls.logo.value;
            this.layoutsService.setLayouts(
              this.clinicSetupService.clinicSetupFormGroup.controls.layouts.value
            );
            this.clinicFormGroup.markAsPristine();
            this.clinicSetupService.clinicSetupFormGroup.markAsPristine();
            this.layoutsService.layoutsFormGroup.markAsPristine();
          }
        });
    }
  }

  close() {
    if (this.hasUnsavedChanges()) {
      const title = this.translocoService.translate('Unsaved Changes');
      const body = this.translocoService.translate('You have unsaved changes. Are you sure you want to discard them?');
      const yesButton = this.translocoService.translate('Continue Editing');
      const noButton = this.translocoService.translate('Discard Changes');

      this.confirmModalService
        .show(title, body, yesButton, noButton, false, false, true)
        .subscribe(confirmed => {
          if (!confirmed) {
            this.dialogRef.close();
          }
        });
    } else {
      this.dialogRef.close();
    }
  }

  getClinicMutation(): Observable<any> {
    if (!this.clinicSetupService.clinicSetupFormGroup.dirty) {
      return this.clinicService.updateClinic(
        this.clinicFormGroup.toUpdateClinicInput(this.data.clinic.id)
      );
    }

    if (!this.data.clinic.clinicSetup) {
      return this.clinicService.updateClinicAndCreateClinicSetup(
        this.clinicFormGroup.toUpdateClinicInput(this.data.clinic.id),
        this.clinicSetupService.clinicSetupFormGroup.getCreateClinicSetupInput(this.data.clinic.id)
      );
    }
    return zip(
      this.clinicService.updateClinic(
        this.clinicFormGroup.toUpdateClinicInput(this.data.clinic.id)
      ),
      this.clinicSetupService.updateClinicSetup(
        this.clinicSetupService.clinicSetupFormGroup.updateClinicSetupInput()
      )
    );
  }

  getReferralCenterSyncMutation(): Observable<any> {
    if (!this.referralService.referralCenter) {
      return of(null);
    }

    let requiresSync = false;

    const updateReferralCenterInput: UpdateReferralCenterInput = {
      id: this.referralService.referralCenter.id
    };

    if (this.referralService.referralCenter.name !== this.clinicFormGroup.controls.name.value) {
      requiresSync = true;
      updateReferralCenterInput.name = this.clinicFormGroup.controls.name.value;
    }

    if (
      !_.isEqual(
        this.referralService.referralCenter.logo,
        this.clinicFormGroup.controls.logo.value
      ) &&
      (!this.clinicFormGroup.controls.logo.value ||
        !this.clinicFormGroup.controls.logo.value.__typename)
    ) {
      requiresSync = true;
      updateReferralCenterInput.logo = this.clinicFormGroup.controls.logo.value;
    }

    return requiresSync
      ? this.referralService.updateReferralCenter(updateReferralCenterInput).pipe(
          tap(() => {
            this.referralService.referralCenter.name = this.clinicFormGroup.controls.name.value;
            this.referralService.referralCenter.logo = this.clinicFormGroup.controls.logo.value;
          })
        )
      : of(null);
  }

  private makeSureFormErrorsAreVisible() {
    // // TODO: Do this without hardcoding the tabs
    // const invalidTab = this.clinicFormGroup.invalid
    //   ? this.clinicFormGroup.controls.emrIntegration.invalid
    //     ? 2
    //     : 0
    //   : -1;
    // if (invalidTab !== -1) {
    //   this.tabGroup.selectedIndex = invalidTab;
    // }
  }

  private enableInteraction() {
    this.dialogRef.disableClose = false;
    this.clinicFormGroup.enable();
  }

  private disableInteraction() {
    this.dialogRef.disableClose = true;
    this.clinicFormGroup.disable();
  }
}
